import { motion } from "framer-motion";
import { Heart, Sparkles, Star } from "lucide-react";
import { useMemo } from "react";

interface FloatingParticlesProps {
  heartStyle: "emoji" | "neon" | "classic" | "mixed";
  intensity: "subtle" | "normal" | "chaos";
  themeColor: "pink" | "red" | "purple" | "roseGold";
}

const FloatingParticles = ({ heartStyle, intensity, themeColor }: FloatingParticlesProps) => {
  const particleCount = {
    subtle: 25,
    normal: 45,
    chaos: 80,
  };

  const colorClasses = {
    pink: { heart: "text-pink-500", fill: "fill-pink-500", glow: "rgba(236,72,153,0.6)" },
    red: { heart: "text-red-500", fill: "fill-red-500", glow: "rgba(239,68,68,0.6)" },
    purple: { heart: "text-purple-500", fill: "fill-purple-500", glow: "rgba(168,85,247,0.6)" },
    roseGold: { heart: "text-amber-400", fill: "fill-amber-400", glow: "rgba(251,191,36,0.6)" },
  };

  const colors = colorClasses[themeColor];

  const emojiHearts = ["💖", "💕", "💗", "💓", "❤️", "💜", "💛", "🧡", "💘", "💝"];
  const sparkleEmojis = ["✨", "⭐", "🌟", "💫", "🌸", "🌹", "🥀"];

  const particles = useMemo(() => 
    Array.from({ length: particleCount[intensity] }, (_, i) => {
      const type = heartStyle === "mixed" 
        ? ["heart", "emoji", "sparkle", "star"][Math.floor(Math.random() * 4)]
        : heartStyle === "emoji" 
          ? (Math.random() > 0.3 ? "emoji" : "sparkle")
          : heartStyle === "neon"
            ? "heart"
            : "heart";

      return {
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 8,
        duration: 12 + Math.random() * 15,
        size: type === "emoji" ? 18 + Math.random() * 24 : 14 + Math.random() * 28,
        opacity: intensity === "subtle" ? 0.2 + Math.random() * 0.3 : 0.3 + Math.random() * 0.5,
        type,
        emoji: type === "emoji" 
          ? emojiHearts[Math.floor(Math.random() * emojiHearts.length)]
          : type === "sparkle"
            ? sparkleEmojis[Math.floor(Math.random() * sparkleEmojis.length)]
            : null,
        wobbleAmount: 30 + Math.random() * 60,
        rotationSpeed: (Math.random() - 0.5) * 720,
      };
    })
  , [intensity, heartStyle]);

  const renderParticle = (particle: typeof particles[0]) => {
    if (particle.type === "emoji" || particle.type === "sparkle") {
      return (
        <span 
          style={{ fontSize: particle.size }}
          className={heartStyle === "neon" ? "drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]" : ""}
        >
          {particle.emoji}
        </span>
      );
    }

    if (particle.type === "star") {
      return (
        <Star
          style={{ width: particle.size * 0.8, height: particle.size * 0.8 }}
          className={`${colors.heart} ${colors.fill}`}
        />
      );
    }

    // Heart
    return (
      <Heart
        style={{ 
          width: particle.size, 
          height: particle.size,
          filter: heartStyle === "neon" 
            ? `drop-shadow(0 0 ${intensity === "chaos" ? "15px" : "8px"} ${colors.glow})`
            : undefined,
        }}
        className={`${colors.heart} ${colors.fill}`}
      />
    );
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute"
          style={{ 
            left: `${particle.x}%`,
            bottom: -60,
            opacity: particle.opacity,
          }}
          animate={{
            y: [0, -window.innerHeight - 150],
            x: [0, Math.sin(particle.id) * particle.wobbleAmount, 0, -Math.sin(particle.id) * particle.wobbleAmount * 0.5, 0],
            rotate: [0, particle.rotationSpeed],
            scale: intensity === "chaos" ? [1, 1.2, 0.8, 1.1, 1] : [1, 1.05, 1],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {renderParticle(particle)}
        </motion.div>
      ))}

      {/* Extra sparkle layer for chaos mode */}
      {intensity === "chaos" && (
        <>
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={`sparkle-${i}`}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0.5, 1.5, 0.5],
              }}
              transition={{
                duration: 1 + Math.random() * 2,
                delay: Math.random() * 5,
                repeat: Infinity,
              }}
            >
              <Sparkles className="w-4 h-4 text-white/50" />
            </motion.div>
          ))}
        </>
      )}
    </div>
  );
};

export default FloatingParticles;
